import { toEnumKey } from '@repo/utils/string-helper';
import { useMemo } from 'react';
import { PAYMENT_TYPES } from '@/constants';
import { usePaymentMethodsWithNetworks } from '@/hooks/usePaymentMethodsWithNetworks';
import { useTenant } from '@/providers/TenantProvider';
import useMaxDepositsCounts from '../useMaxDepositsCounts';

const useEnhancedPaymentMethods = () => {
  const {
    tenantConfig: { paymentMethods },
  } = useTenant();

  const { creditMethods = [] } = usePaymentMethodsWithNetworks({
    paymentType: PAYMENT_TYPES.DEPOSITS,
  });

  const thresholdProviders = useMemo(() => {
    return creditMethods.filter((provider) => {
      const gatewayKey = toEnumKey(provider.name);
      const entry = paymentMethods.deposit?.find(
        (e) => e?.status === 'ACTIVE' && e?.gateway === gatewayKey && e?.gatewayConfig?.paymentMethodThresholds?.length
      );
      return Boolean(entry);
    });
  }, [creditMethods, paymentMethods.deposit]);

  const thresholdProviderIds = useMemo(() => thresholdProviders.map((p) => p.providerId), [thresholdProviders]);
  const { data: counts = {}, isLoading, isError } = useMaxDepositsCounts(thresholdProviderIds);

  const enhancedMethods = useMemo(() => {
    return creditMethods.flatMap((provider) => {
      const gatewayKey = toEnumKey(provider.name);
      const entry = paymentMethods.deposit?.find((e) => e?.status === 'ACTIVE' && e?.gateway === gatewayKey);
      if (!entry) return [];

      if (entry.currencies && !entry.currencies.includes(provider.currencyCode)) {
        return [];
      }

      const thresholds = entry.gatewayConfig?.paymentMethodThresholds;
      if (!thresholds) return [provider];

      if (isLoading || isError) {
        return [];
      }

      const maxCount = counts[provider.providerId] ?? 0;
      return thresholds
        .filter(({ minDeposits, maxDeposits }) => {
          const meetsMin = maxCount >= minDeposits;
          const meetsMax = maxDeposits == null || maxCount <= maxDeposits;
          return meetsMin && meetsMax;
        })
        .map(({ method }) => ({
          ...provider,
          paymentGatewayMethod: method,
        }));
    });
  }, [creditMethods, paymentMethods.deposit, counts, isLoading, isError]);

  return { enhancedMethods, isLoading };
};

export default useEnhancedPaymentMethods;
